/**
 * Timezone utility - formats dates according to user's timezone setting.
 * Timezone is stored in the user's company profile and cached in localStorage.
 */

const TIMEZONE_KEY = 'wisecall_timezone';
const DEFAULT_TIMEZONE = 'Europe/Berlin';

/** Get current timezone from localStorage cache */
export function getUserTimezone() {
  return localStorage.getItem(TIMEZONE_KEY) || DEFAULT_TIMEZONE;
}

/** Save timezone to localStorage cache (called when settings are loaded/saved) */
export function setUserTimezone(tz) {
  if (tz) localStorage.setItem(TIMEZONE_KEY, tz);
}

/** Format a date string or Date object to the user's timezone */
export function formatDateTime(dateInput, options = {}) {
  if (!dateInput) return '-';
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return '-';
    const tz = getUserTimezone();
    return date.toLocaleString('de-DE', {
      timeZone: tz,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...options,
    });
  } catch {
    return String(dateInput);
  }
}

/** Format date only (no time) */
export function formatDateOnly(dateInput) {
  if (!dateInput) return '-';
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString('de-DE', {
      timeZone: getUserTimezone(),
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return String(dateInput);
  }
}

/** Format short date (day.month + time) for dashboard cards */
export function formatDateShort(dateInput) {
  if (!dateInput) return '';
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return '';
    return date.toLocaleString('de-DE', {
      timeZone: getUserTimezone(),
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

/** Common European timezone options */
export const TIMEZONE_OPTIONS = [
  { value: 'Europe/Berlin', label: 'Berlin (MEZ/MESZ)' },
  { value: 'Europe/Vienna', label: 'Wien (MEZ/MESZ)' },
  { value: 'Europe/Zurich', label: 'Zürich (MEZ/MESZ)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (MEZ/MESZ)' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam (MEZ/MESZ)' },
  { value: 'Europe/Madrid', label: 'Madrid (MEZ/MESZ)' },
  { value: 'Europe/Rome', label: 'Rom (MEZ/MESZ)' },
  { value: 'Europe/Warsaw', label: 'Warschau (MEZ/MESZ)' },
  { value: 'Europe/Istanbul', label: 'Istanbul (TRT)' },
  { value: 'Europe/Moscow', label: 'Moskau (MSK)' },
  { value: 'US/Eastern', label: 'New York (EST/EDT)' },
  { value: 'US/Central', label: 'Chicago (CST/CDT)' },
  { value: 'US/Pacific', label: 'Los Angeles (PST/PDT)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Tokyo', label: 'Tokio (JST)' },
  { value: 'UTC', label: 'UTC' },
];
